import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { guardarConConexion } from '../../../nucleo/api/cliente.js';
import { BandaDeRestauracion, MarcaDeBorrador } from '../../../nucleo/borradores/BandaDeBorrador.jsx';
import { FORMULARIOS_CON_BORRADOR } from '../../../nucleo/borradores/almacenDeBorradores.js';
import { useBorrador } from '../../../nucleo/borradores/useBorrador.js';
import { consultarMonedas } from '../../../nucleo/api/nucleoApi.js';
import { formatearFecha, obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { CampoDeImporte } from '../../../nucleo/importes/CampoDeImporte.jsx';
import { CintaDeVersionNueva } from '../../../nucleo/pwa/CintaDeVersionNueva.jsx';
import { useContextoDeRegreso } from '../../../nucleo/navegacion/useContextoDeRegreso.js';
import { useSesion } from '../../../nucleo/sesion/ProveedorDeSesion.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import { ConfirmacionGeneral, TONOS_DE_CONFIRMACION } from '../../../nucleo/componentes/ConfirmacionGeneral.jsx';
import { formatearImporte } from '../../../nucleo/importes/formatoDeImporte.js';
import {
  advertenciaDelResultado,
  calcularVistaPrevia,
  ESTADOS_DEL_RESULTADO,
  textosDeLaConfirmacion,
} from '../logica/correccionDeSaldoInicial.js';
import {
  actualizarCuenta,
  consultarTiposDeCuenta,
  crearCuenta,
  obtenerCuenta,
  simularFechasDeTarjeta,
} from '../api/cuentasApi.js';
import { RepartoDeLaDeuda } from '../../deuda-descompuesta/componentes/RepartoDeLaDeuda.jsx';
import {
  diferidoGuardadoFueraDeRango,
  repartoDeLaDeuda,
} from '../../deuda-descompuesta/logica/deudaDescompuesta.js';

const CLAVE_EFECTIVO = 'EFECTIVO';
const CLAVE_CREDITO = 'CREDITO';
const CLAVE_PRESTAMO = 'PRESTAMO';

/**
 * RN-752 (incremento 20/21) · LOS TIPOS QUE SÍ TIENEN PLÁSTICO, POR LISTA
 * EXPLÍCITA. Es el espejo exacto de `TiposDeCuenta.AdmitePlastico` del backend
 * (Cuenta.cs: Débito, Ahorro y Crédito — sin Préstamo), y por eso se escribe como
 * lista de los que SÍ y no como negación contra una sola clave.
 *
 * ESTA ERA LA CAUSA DEL HALLAZGO: la condición del bloque del plástico preguntaba
 * «¿no es efectivo?», que era cierto mientras Efectivo fue el único tipo sin
 * plástico. El día que nació el tipo Préstamo la pregunta dejó de ser correcta y la
 * pantalla ofrecía capturar cuatro dígitos y vigencia en un préstamo, contra lo que
 * RN-752 prohíbe por nombre y contra lo que el servidor rechaza con
 * DATOS_DE_TARJETA_NO_APLICAN. Con la lista explícita, un tipo nuevo queda FUERA
 * hasta que alguien lo añada a propósito, que es el comportamiento seguro (D-428c).
 */
const CLAVES_CON_PLASTICO = ['DEBITO', 'AHORRO', CLAVE_CREDITO];

/**
 * D-470 / RN-793 (incremento 21/21) · LOS TIPOS QUE EL ALTA GENÉRICA SÍ OFRECE,
 * POR LISTA EXPLÍCITA DE LOS QUE SÍ — nunca por exclusión (D-428c).
 *
 * POR QUÉ PRÉSTAMO NO ESTÁ AQUÍ, y no es un olvido: un préstamo nace por el alta
 * en DOS PASOS (`AltaDePrestamo.jsx`), porque el paso 1 captura **el monto
 * original y cuánto debes hoy juntos**, y de que ambas cifras convivan dependen la
 * cota `0 < saldo ≤ monto original`, el aviso cuando se omite el saldo y el
 * encadenamiento al paso 2 (D-459, RN-739d, RN-780). El alta genérica no captura
 * monto original, así que crear un préstamo por aquí se saltaría las tres cosas:
 * es exactamente la segunda vía de nacimiento que D-459 cerró.
 *
 * EN EL SERVIDOR NO CAMBIA NADA: el tipo 5 sigue siendo válido en la API del 2/6
 * porque el paso 1 la usa. Esto es regla de PANTALLA, el mismo carácter de ayuda
 * dirigida con que esta misma pantalla ya oculta el plástico (RN-752); la
 * validación de verdad sigue donde ya estaba.
 *
 * EN EDICIÓN NO APLICA ESTA LISTA: una cuenta préstamo enseña su tipo fijo y las
 * demás no listan Préstamo como destino, porque el cambio de tipo hacia y desde
 * Préstamo está prohibido en el servidor (D-471, RN-794).
 */
const CLAVES_DEL_ALTA_GENERICA = ['EFECTIVO', 'DEBITO', 'AHORRO', CLAVE_CREDITO];

/** Tope del motivo, el MISMO de las notas de la cuenta (RN-558). */
const LONGITUD_MAXIMA_DEL_MOTIVO = 500;

const FORMULARIO_VACIO = {
  nombre: '',
  idTipoCuenta: '',
  idMoneda: '',
  saldoInicial: '0.00',
  cuatroUltimosDigitos: '',
  vigenciaMes: '',
  vigenciaAnio: '',
  limiteCredito: '',
  diaCorte: '',
  diaLimitePago: '',
  notas: '',
  /*
   * H-17 · MOTIVO DE LA CORRECCIÓN DEL SALDO INICIAL (RF-185, RN-558 a RN-561).
   * Texto libre OPCIONAL de hasta 500 caracteres. NO ES LA NOTA DE LA CUENTA: son
   * dos campos distintos con dos vidas distintas y queda PROHIBIDO mezclarlos
   * (RN-561). Vive en el formulario —y por tanto en el borrador— porque lo que la
   * persona escribe no se le puede perder al rotar el teléfono.
   */
  motivoDeCorreccion: '',
  /*
   * DIFERIDO DECLARADO (incremento 18/21, RF-248, RF-249, D-389).
   *
   * UN SOLO IMPORTE TOTAL POR TARJETA, sin desglose por compra: el desglose fino
   * llega en el 19 con los planes reales. Vive en el formulario —y por tanto en el
   * borrador— porque lo que la persona escribe no se le puede perder al rotar el
   * teléfono (RN-144).
   *
   * ARRANCA VACÍO y se rellena al cargar la cuenta. Vacío significa «no lo
   * toques»: así una edición de nombre no borra en silencio el diferido declarado
   * (RN-686).
   */
  diferidoDeclarado: '0.00',
};

/**
 * RNF-133 · Los importes de esta pantalla que CONSERVAN SU VALOR y que, por eso,
 * no se pisan cuando la respuesta de la cuenta llega tarde (RN-661, CA-18-26).
 * Son los dos que el incremento 18 declara: la deuda inicial y el diferido.
 */
const IMPORTES_QUE_MANDAN_SI_SE_TECLEAN = ['saldoInicial', 'diferidoDeclarado'];

/**
 * Alta y edición de una cuenta o tarjeta (RF-036, RF-037, RF-038).
 *
 * LO QUE ESTA PANTALLA NO DECIDE, y por eso no lo calcula ni lo inventa:
 *   * El propietario NO se captura: lo asigna el servidor con el usuario en sesión
 *     (RN-029). No hay campo y no se envía.
 *   * `esCredito` no es campo: el servidor lo copia del tipo elegido (RN-054).
 *   * Los DÍAS DE GRACIA no se capturan, se calculan y se muestran informativos
 *     (D-21, RN-056).
 *   * NO hay campo de orden ni pantalla de reordenar: el orden es alfabético y lo
 *     resuelve el servidor (D-29, RN-071). Si `orden` llegara, el servidor
 *     respondería CAMPO_NO_ADMITIDO.
 *   * Los cuatro dígitos y la vigencia se piden SOLO en los tipos con plástico
 *     —Débito, Ahorro y Crédito, por lista explícita— y por tanto no en EFECTIVO ni
 *     en PRÉSTAMO (D-28, RN-070g.iv, precisado por RN-752).
 *   * MONEDA Y TIPO se muestran deshabilitados cuando la cuenta ya tiene
 *     movimientos, PERO el control es del servidor: deshabilitar el campo es ayuda
 *     visual (D-23, RN-052b).
 *   * EL SALDO INICIAL NO: desde el incremento 11/16 se corrige SIEMPRE, tenga la
 *     cuenta los movimientos que tenga (H-17, RF-171, D-292, RN-533). Toda
 *     corrección deja asiento consultable con quién, cuándo y de cuánto a cuánto
 *     (RF-175, RF-176).
 */
export default function EditarCuenta() {
  const { idCuenta } = useParams();
  const [parametros] = useSearchParams();
  const navegar = useNavigate();
  const { sesion } = useSesion();

  const esAlta = !idCuenta;
  const [tipos, setTipos] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [cuenta, setCuenta] = useState(null);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const [formulario, setFormulario] = useState(FORMULARIO_VACIO);

  // VALORES INICIALES CON LOS QUE SE ABRIÓ EL FORMULARIO (RN-128). Es la referencia
  // contra la que se decide si hay captura en curso: sin ella no se puede
  // distinguir "abierto sin cambios" (RN-129) de "sucio". Queda en null mientras
  // los catálogos —o el registro que se edita— no han llegado.
  const [iniciales, setIniciales] = useState(null);

  /*
   * RNF-133 / RN-661 · Se anota QUÉ IMPORTES ya tocó la persona, para que la
   * respuesta de la cuenta que llega después no los pise (CA-18-26). Es un ref y
   * no un estado a propósito: no debe provocar un pintado, solo recordar.
   */
  const importesTecleados = useRef(new Set());

  useEffect(() => {
    Promise.all([consultarTiposDeCuenta(), consultarMonedas(true)])
      .then(([listaTipos, listaMonedas]) => {
        setTipos(listaTipos);
        setMonedas(listaMonedas);

        if (esAlta) {
          const quiereCredito = parametros.get('credito') === '1';
          /*
           * D-470 · El tipo PROPUESTO también sale de la lista del alta genérica. El
           * respaldo `?? ofrecidos[0]` era `?? listaTipos[0]`, y con Préstamo en el
           * catálogo eso podía dejar el selector proponiendo justo el tipo que esta
           * pantalla ya no ofrece: la pantalla enseñaría «Débito» y el formulario
           * llevaría otro valor. Se toma siempre de los ofrecidos (RN-793).
           */
          const ofrecidos = listaTipos.filter((t) => CLAVES_DEL_ALTA_GENERICA.includes(t.clave));
          const tipoPropuesto = ofrecidos.find((t) => (quiereCredito ? t.clave === CLAVE_CREDITO : t.clave === 'DEBITO'))
            ?? ofrecidos[0];
          const base = {
            ...FORMULARIO_VACIO,
            idTipoCuenta: String(tipoPropuesto?.idTipoCuenta ?? ''),
            // Se propone la moneda predeterminada de quien captura (RN-038).
            idMoneda: String(sesion?.idMonedaPredeterminada ?? listaMonedas[0]?.idMoneda ?? ''),
          };
          // Se MEZCLA sobre lo que ya hubiera en pantalla: si la persona alcanzó a
          // escribir antes de que llegaran los catálogos, no se le borra lo
          // escrito (D-80). `iniciales` es la base limpia contra la que se decide
          // si hay captura en curso (RN-128).
          setFormulario((previo) => ({ ...previo, idTipoCuenta: base.idTipoCuenta, idMoneda: base.idMoneda }));
          setIniciales(base);
        }
      })
      .catch(setError);
  }, [esAlta, parametros, sesion]);

  useEffect(() => {
    if (esAlta) return;
    obtenerCuenta(idCuenta)
      .then((datos) => {
        setCuenta(datos);
        const cargado = {
          nombre: datos.nombre,
          idTipoCuenta: String(datos.idTipoCuenta),
          idMoneda: String(datos.idMoneda),
          // La vía de RN-069d: si la cuenta se capturó por error, se corrige el
          // saldo inicial a cero y entonces se puede desactivar.
          saldoInicial: parametros.get('cero') === '1' ? '0.00' : datos.saldoInicial,
          cuatroUltimosDigitos: datos.cuatroUltimosDigitos ?? '',
          vigenciaMes: datos.vigenciaMes ?? '',
          vigenciaAnio: datos.vigenciaAnio ?? '',
          limiteCredito: datos.limiteCredito ?? '',
          diaCorte: datos.diaCorte ?? '',
          diaLimitePago: datos.diaLimitePago ?? '',
          notas: datos.notas ?? '',
          // El motivo arranca VACÍO siempre: es de ESTA corrección, no un dato de la
          // cuenta, y por eso no se lee del registro (RN-560).
          motivoDeCorreccion: '',
          /*
           * DIFERIDO DECLARADO (RF-249). Llega del servidor y SE PRECARGA: el
           * campo nunca se abre vacío teniendo valor guardado (RNF-133, RN-659).
           * En una cuenta que no es de crédito el servidor manda "0.00" y el campo
           * no se ofrece.
           */
          diferidoDeclarado: datos.diferidoDeclarado ?? '0.00',
        };
        /*
         * RNF-133 / RN-661 / CA-18-26 · LO TECLEADO MANDA SOBRE EL DATO QUE LLEGA
         * TARDE. Antes de esto, la respuesta de la cuenta reemplazaba el formulario
         * completo: quien alcanzaba a escribir un importe mientras cargaba lo veía
         * desaparecer. Ahora los dos importes del incremento se conservan si ya se
         * tocaron, y el resto del registro se aplica igual.
         *
         * El campo NO se deshabilita ni gana rótulo de carga mientras el dato
         * viaja (RN-684, D-375): un campo deshabilitado volvería imposible el caso
         * que RN-661 exige posible.
         */
        setFormulario((previo) => {
          const mezclado = { ...cargado };
          for (const campo of IMPORTES_QUE_MANDAN_SI_SE_TECLEAN) {
            if (importesTecleados.current.has(campo)) mezclado[campo] = previo[campo];
          }
          return mezclado;
        });
        // `iniciales` es la base LIMPIA del registro, no la mezcla: es la
        // referencia contra la que se decide si hay captura en curso (RN-128).
        setIniciales(cargado);
      })
      .catch(setError);
  }, [esAlta, idCuenta, parametros]);

  const tipoElegido = tipos.find((t) => String(t.idTipoCuenta) === formulario.idTipoCuenta);
  /*
   * RN-752 (incremento 20/21) · LISTA EXPLÍCITA DE TIPO, nunca la bandera.
   *
   * Esta constante decide los CAMPOS EXCLUSIVOS DE TARJETA —límite de crédito, día
   * de corte, día de pago y diferido declarado—, y preguntar por `esCredito` dejó de
   * ser correcto el día que nació el tipo Préstamo: también lo lleva en verdadero
   * —dice «es pasivo»— y un préstamo NO TIENE ninguno de esos cuatro datos; el
   * modelo los prohíbe (CK de tres ramas, D-434) y el servidor los rechaza.
   *
   * Para los cuatro tipos de siempre el valor es IDÉNTICO: entre ellos, el único con
   * `esCredito` es la tarjeta de crédito.
   */
  const esCredito = tipoElegido?.clave === CLAVE_CREDITO;
  const esEfectivo = tipoElegido?.clave === CLAVE_EFECTIVO;
  /*
   * RN-752 · LO QUE DECIDE LA SUPERFICIE DEL PLÁSTICO —cuatro últimos dígitos y
   * vigencia—, por lista explícita de tipo y nunca por `!esEfectivo`. Mismo criterio
   * que `esTarjeta` en DetalleDeCuenta.jsx y que `esTarjetaDeCredito` en
   * BloqueDeMsiDeLaTarjeta.jsx y DeudaDescompuestaDeLaTarjeta.jsx, y mismo predicado
   * que `TiposDeCuenta.AdmitePlastico` del servidor. Para los cuatro tipos de siempre
   * el valor es IDÉNTICO al de antes: lo único que cambia es que Préstamo —y cualquier
   * tipo futuro— queda fuera.
   */
  const admitePlastico = CLAVES_CON_PLASTICO.includes(tipoElegido?.clave);

  /*
   * D-470 / D-471 · QUÉ TIPOS SE OFRECEN EN ESTE SELECTOR, según el momento.
   *
   *   · ALTA ........... los cuatro de CLAVES_DEL_ALTA_GENERICA. Préstamo sale, y
   *     en su lugar vive el enlace al alta en dos pasos.
   *   · EDICIÓN DE UN PRÉSTAMO ... solo su propio tipo. No es un selector con una
   *     opción por capricho: es el campo enseñando un dato que NO se puede cambiar
   *     (RN-794), en el mismo sitio donde la persona lo busca.
   *   · EDICIÓN DE OTRO TIPO ..... los cuatro, sin Préstamo como destino.
   *
   * SE DERIVA DEL CATÁLOGO QUE YA LLEGÓ, no de una lista de nombres dibujada a
   * mano: los rótulos los sigue poniendo el servidor (RN-049c).
   */
  const esPrestamoEnEdicion = !esAlta && cuenta?.claveTipoCuenta === CLAVE_PRESTAMO;
  const tiposOfrecidos = esPrestamoEnEdicion
    ? tipos.filter((t) => t.clave === CLAVE_PRESTAMO)
    : tipos.filter((t) => CLAVES_DEL_ALTA_GENERICA.includes(t.clave));

  /*
   * CÓMO SE LLAMA ESTE REGISTRO EN PANTALLA, también por lista explícita (RN-752).
   * Decidirlo con `cuenta.esCredito` titulaba «Editar tarjeta» un préstamo —la bandera
   * solo dice «es pasivo»— y contradecía la acción «Editar préstamo» desde la que se
   * llega (DetalleDeCuenta.jsx). En el alta no hay registro todavía y el título es el
   * suyo, «Nueva cuenta», sin cambio.
   */
  const rotuloDelRegistro = cuenta?.claveTipoCuenta === CLAVE_CREDITO
    ? 'tarjeta'
    : (cuenta?.claveTipoCuenta === CLAVE_PRESTAMO ? 'préstamo' : 'cuenta');

  /*
   * EL INDICADOR ÚNICO SE PARTIÓ EN DOS (D-292, RN-533) — RIESGO Nº 1 DE H-17.
   *
   * Hasta el 2026-10-05 esta pantalla tenía UNA constante, `congelado`, que valía
   * `!cuenta.saldoInicialEditable` y deshabilitaba TIPO, MONEDA y SALDO INICIAL a la
   * vez. Al descongelar solo el saldo inicial, dejar esa constante como estaba
   * habría descongelado de rebote moneda y tipo, que D-23 MANTIENE CONGELADOS.
   *
   * Desde aquí:
   *   · el SALDO INICIAL ya no se deshabilita nunca (RF-171);
   *   · `monedaYTipoCongelado` —leído del campo nuevo `monedaYTipoEditables` del
   *     DTO— es lo único que deshabilita los dos selectores, y conserva sus dos
   *     textos palabra por palabra porque ahí siguen siendo verdad (RN-535).
   */
  const monedaYTipoCongelado = cuenta ? !cuenta.monedaYTipoEditables : false;
  const teniaPlastico = Boolean(cuenta?.cuatroUltimosDigitos || cuenta?.vigenciaMes);

  /*
   * H-17 · VISTA PREVIA DEL EFECTO DE LA CORRECCIÓN (RF-173). La aritmética y los
   * textos viven en `logica/correccionDeSaldoInicial.js`: esta pantalla no decide
   * nada y no inventa ninguna cifra. No se llama al servidor para esto.
   */
  const vistaPrevia = useMemo(
    () => (esAlta || !cuenta ? null : calcularVistaPrevia(cuenta, formulario.saldoInicial)),
    [esAlta, cuenta, formulario.saldoInicial],
  );
  const hayCorreccion = Boolean(vistaPrevia?.hayCambio);
  const advertencia = useMemo(
    () => (hayCorreccion ? advertenciaDelResultado(cuenta, vistaPrevia) : null),
    [hayCorreccion, cuenta, vistaPrevia],
  );

  /*
   * RN-560 · EL CAMPO DEL MOTIVO SE OCULTA Y SE LIMPIA en cuanto el importe vuelve a
   * su valor original: así no se puede escribir un motivo que acabe en ningún sitio.
   */
  useEffect(() => {
    if (hayCorreccion) return;
    setFormulario((previo) => (previo.motivoDeCorreccion === ''
      ? previo
      : { ...previo, motivoDeCorreccion: '' }));
  }, [hayCorreccion]);

  /*
   * DEUDA DESCOMPUESTA (incremento 18/21, RF-248, RF-249, RN-687, RN-688).
   *
   * La aritmética vive en `deuda-descompuesta/logica`: esta pantalla no decide y
   * no inventa ninguna cifra. Y NO VALIDA: el aviso de abajo existe para que la
   * persona no descubra el rechazo al guardar, pero LA VALIDACIÓN ES DEL SERVIDOR
   * (RN-688, D-390) y es él quien rechaza con su código.
   *
   * LA COTA SE EVALÚA CONTRA LA DEUDA TOTAL RESULTANTE, no contra la deuda inicial
   * (D-390): en el alta las dos coinciden —la cuenta nace sin movimientos— y en la
   * corrección la deuda total ya viene en la respuesta del servidor.
   */
  const reparto = useMemo(
    () => (esCredito
      ? repartoDeLaDeuda({
        cuenta: esAlta ? null : cuenta,
        saldoInicialEscrito: formulario.saldoInicial,
        diferidoEscrito: formulario.diferidoDeclarado,
      })
      : null),
    [esCredito, esAlta, cuenta, formulario.saldoInicial, formulario.diferidoDeclarado],
  );

  // RN-706 / D-391 · El diferido GUARDADO quedó por encima de la deuda porque la
  // deuda bajó después. No es error, no bloquea y nada se ajustó solo.
  const diferidoFueraDeRango = useMemo(
    () => (esAlta ? false : diferidoGuardadoFueraDeRango(cuenta)),
    [esAlta, cuenta],
  );

  const motivoEscrito = (formulario.motivoDeCorreccion ?? '').trim();
  const motivoSePasa = motivoEscrito.length > LONGITUD_MAXIMA_DEL_MOTIVO;
  const noCabeElDiferido = reparto?.cabe === false;

  const cambiar = (campo) => (evento) => setFormulario((previo) => ({ ...previo, [campo]: evento.target.value }));

  /*
   * CAMPOS DE IMPORTE (RF-086). La regla es única y vive en
   * `nucleo/importes/CampoDeImporte.jsx`: esta pantalla no formatea, no valida
   * decimales y no decide nada sobre el signo. Sólo dice CUÁL es la divisa del
   * contexto —la de la cuenta (RN-051)— y recoge el valor.
   *
   * Al cambiar de moneda el importe capturado NO se convierte: se vuelve a
   * presentar con el código nuevo, que es justo lo que evita leer un importe como
   * si fuera de la otra divisa (RN-138).
   */
  const divisaDelContexto = monedas.find((m) => String(m.idMoneda) === formulario.idMoneda)?.codigoISO ?? null;

  const cambiarImporte = (campo) => (valorNuevo) => {
    importesTecleados.current.add(campo);
    setFormulario((previo) => ({ ...previo, [campo]: valorNuevo }));
  };

  // Guardar con un importe marcado como inválido NO procede: el error se mantiene
  // y no se manda nada al servidor.
  const [importesInvalidos, setImportesInvalidos] = useState({});
  const marcarValidez = useCallback((campo) => (esValido) => {
    setImportesInvalidos((previo) => (
      Boolean(previo[campo]) === !esValido ? previo : { ...previo, [campo]: !esValido }
    ));
  }, []);
  const hayImporteInvalido = Object.values(importesInvalidos).some(Boolean);

  /*
   * BORRADOR PERSISTIDO DE ESTA CAPTURA (RF-084, D-85, RN-144).
   *
   * El TIPO DE FORMULARIO de la clave se fija AL ABRIR y ya no cambia: alta de
   * tarjeta y alta de cuenta entran por rutas distintas y en edición lo dice el
   * registro. Si la clave siguiera al campo "Tipo", cambiarlo a media captura
   * movería el borrador a otra clave y se perdería lo escrito, que es justo lo que
   * este lote impide.
   */
  const formularioDeBorrador = (esAlta ? parametros.get('credito') === '1' : cuenta?.esCredito === true)
    ? FORMULARIOS_CON_BORRADOR.TARJETA
    : FORMULARIOS_CON_BORRADOR.CUENTA;

  const restaurarFormulario = useCallback((valores) => setFormulario(valores), []);

  const borrador = useBorrador({
    formulario: formularioDeBorrador,
    idRegistro: esAlta ? null : idCuenta,
    idUsuario: sesion?.idUsuario ?? null,
    valores: formulario,
    valoresIniciales: iniciales,
    alRestaurar: restaurarFormulario,
  });

  const [confirmandoCancelacion, setConfirmandoCancelacion] = useState(false);

  /*
   * REGRESO AL CONTEXTO DE ORIGEN (RF-146, CR-01, RN-391).
   *
   * [CAMBIADO 2026-10-03 · incremento 09] El destino de salida del alta estaba
   * FIJO en `/cuentas` y perdía la pestaña Tarjetas: quien registraba una tarjeta
   * desde la pestaña Tarjetas aterrizaba en la pestaña Cuentas. Ahora se vuelve a
   * la pestaña desde la que se abrió el alta. En la EDICIÓN el destino sigue
   * siendo el detalle de la cuenta, que es su padre y es el correcto.
   */
  const { destinoDeRegreso } = useContextoDeRegreso('/cuentas');
  const destinoAlSalir = useMemo(
    () => (esAlta ? destinoDeRegreso : `/cuentas/${idCuenta}`),
    [esAlta, idCuenta, destinoDeRegreso],
  );

  /**
   * CANCELAR es la única forma de perder lo escrito a propósito (RN-134b): borra el
   * borrador. Por eso se confirma. Sin cambios no hay nada que perder y no se
   * pregunta (RN-129).
   */
  function cancelar() {
    if (borrador.sucio) {
      setConfirmandoCancelacion(true);
      return;
    }
    /* REEMPLAZA el paso del formulario, no lo deja debajo (RF-148, RN-391). */
    navegar(destinoAlSalir, { replace: true });
  }

  function confirmarCancelacion() {
    borrador.borrarPorCancelacion();
    setConfirmandoCancelacion(false);
    navegar(destinoAlSalir, { replace: true });
  }

  // AVISO DE DÍAS DE GRACIA (RF-037) EN MODO SIMULACIÓN (D-31, RN-056d).
  //
  // Esta pantalla NO calcula fechas ni días de gracia: se los pide al servidor con
  // los días que la persona acaba de teclear y con la fecha de negocio del
  // dispositivo. Aproximar con un mes de 30 días está PROHIBIDO (RN-056b) y una
  // segunda implementación del fin de mes en el cliente estaría mal tarde o
  // temprano (punto 22 del núcleo compartido).
  const [simulacion, setSimulacion] = useState(null);

  useEffect(() => {
    const corte = Number(formulario.diaCorte);
    const pago = Number(formulario.diaLimitePago);

    if (!esCredito || !corte || !pago || corte < 1 || corte > 31 || pago < 1 || pago > 31) {
      setSimulacion(null);
      return undefined;
    }

    let vigente = true;
    simularFechasDeTarjeta({ diaCorte: corte, diaLimitePago: pago, fechaReferencia: obtenerFechaDeNegocioHoy() })
      .then((datos) => vigente && setSimulacion(datos))
      .catch(() => vigente && setSimulacion(null));

    return () => { vigente = false; };
  }, [esCredito, formulario.diaCorte, formulario.diaLimitePago]);

  /*
   * H-17 · EL GUARDADO PASA POR LA CONFIRMACIÓN CUANDO —Y SOLO CUANDO— HAY
   * CORRECCIÓN DEL SALDO INICIAL (RF-174, RN-538).
   *
   * Si el importe de arranque no cambió no se pregunta nada: sin corrección no hay
   * nada que confirmar, y preguntar por un renombre sería ruido.
   *
   * SE REUTILIZA EL COMPONENTE GENÉRICO DEL NÚCLEO creado en el incremento 10
   * (D-277, D-278), con su contrato de uso de §7 bis de `requerimientos-categorias.md`:
   * no se construye un diálogo nuevo (RN-503). El error del guardado NO vive dentro
   * de la pieza: lo presenta esta pantalla con el aviso único del 08 (RN-505).
   */
  const [confirmandoCorreccion, setConfirmandoCorreccion] = useState(false);

  function intentarGuardar(evento) {
    evento.preventDefault();
    // No se manda al servidor una cifra que ya sabemos que no cabe (RN-688): el
    // aviso en rojo sigue a la vista. El servidor la rechazaría igual —ahí está la
    // validación de verdad— y esto solo evita el viaje.
    if (hayImporteInvalido || motivoSePasa || noCabeElDiferido) return;

    if (hayCorreccion) {
      setConfirmandoCorreccion(true);
      return;
    }

    guardar();
  }

  async function guardar() {
    // Con un importe marcado como inválido no se guarda: el error se mantiene a la
    // vista y nada viaja al servidor (RN-148b, RN-149).
    // No se manda al servidor una cifra que ya sabemos que no cabe (RN-688): el
    // aviso en rojo sigue a la vista. El servidor la rechazaría igual —ahí está la
    // validación de verdad— y esto solo evita el viaje.
    if (hayImporteInvalido || motivoSePasa || noCabeElDiferido) return;
    setError(null);
    setGuardando(true);

    // Al cambiar el tipo a Efectivo, los datos del plástico se borran en la misma
    // operación (RN-070g.vi): no se envían, y el servidor los limpia.
    const peticion = {
      nombre: formulario.nombre,
      idTipoCuenta: Number(formulario.idTipoCuenta),
      idMoneda: Number(formulario.idMoneda),
      saldoInicial: String(formulario.saldoInicial),
      // RN-752 · MISMA LISTA EXPLÍCITA QUE DECIDE SI SE OFRECEN LOS CAMPOS: si el tipo
      // no admite plástico, los tres datos viajan nulos. Con `!esEfectivo` un préstamo
      // podía mandar lo que quedara en el formulario y recibir
      // DATOS_DE_TARJETA_NO_APLICAN; nulo es también lo que hace que el servidor los
      // limpie al cambiar a un tipo sin plástico (RN-070g.vi).
      cuatroUltimosDigitos: admitePlastico ? (formulario.cuatroUltimosDigitos || null) : null,
      vigenciaMes: admitePlastico && formulario.vigenciaMes ? Number(formulario.vigenciaMes) : null,
      vigenciaAnio: admitePlastico && formulario.vigenciaAnio ? Number(formulario.vigenciaAnio) : null,
      limiteCredito: esCredito ? String(formulario.limiteCredito) : null,
      diaCorte: esCredito && formulario.diaCorte ? Number(formulario.diaCorte) : null,
      diaLimitePago: esCredito && formulario.diaLimitePago ? Number(formulario.diaLimitePago) : null,
      notas: formulario.notas || null,
      version: cuenta?.version,
      /*
       * RN-560 · Solo viaja si hay corrección de verdad. El servidor lo ignoraría
       * igual —un motivo sin corrección no se graba—, pero no mandarlo deja claro en
       * la petición que esta edición no es una corrección.
       * RN-561 · Va en SU PROPIO campo. No se concatena a `notas` ni se copia a él.
       */
      motivoDeCorreccionDeSaldoInicial: hayCorreccion ? (motivoEscrito || null) : null,
      /*
       * DIFERIDO DECLARADO (RF-248, RF-249, RN-690). SOLO VIAJA EN TARJETA: en una
       * cuenta que no es de crédito el campo no se ofrece y no se envía, porque
       * enviarlo se rechaza con razón (CA-18-15).
       *
       * Se manda como cadena decimal (RNF-001). Nulo significa «no lo toques» y es
       * lo que conserva el valor vigente del servidor (RN-686).
       */
      diferidoDeclarado: esCredito ? String(formulario.diferidoDeclarado ?? '0.00') : null,
    };

    try {
      const guardada = await guardarConConexion(() => (esAlta
        ? crearCuenta(peticion)
        : actualizarCuenta(idCuenta, peticion)));

      // RN-134a: el borrador se borra al guardar CON ÉXITO, nunca antes. Si el
      // servidor rechaza, lo capturado sigue resguardado en este dispositivo.
      borrador.borrarPorGuardado();
      /*
       * RF-148, RN-391, RN-395. Antes EMPUJABA, así que un toque del botón atrás
       * del teléfono devolvía al formulario ya guardado. Ahora REEMPLAZA el paso
       * del formulario: un toque atrás deja a la persona en la pantalla que había
       * ANTES del formulario —la pestaña de origen—, no en el formulario.
       * Se reemplaza el paso del formulario, NUNCA el del origen (RN-396).
       * El detalle al que se llega ya devuelve a su pestaña: en una tarjeta, su
       * flecha va a `/cuentas/tarjetas` (RF-146).
       */
      navegar(`/cuentas/${guardada.idCuenta}`, { replace: true });
    } catch (fallo) {
      /*
       * RN-505 · El error sale AQUÍ ARRIBA, en el aviso único de la pantalla, no
       * dentro de la confirmación. La pieza se cierra primero para que el aviso se
       * vea: dejarla abierta con un error detrás es lo que el 08 vino a evitar.
       * RN-543 · Si la cuenta cambió mientras se editaba, el servidor responde
       * CONFLICTO_CONCURRENCIA y no se pisa el cambio de nadie.
       */
      setConfirmandoCorreccion(false);
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Shell
      titulo={esAlta ? 'Nueva cuenta' : `Editar ${rotuloDelRegistro}`}
      /* La flecha de volver sale al mismo sitio que Cancelar (RF-146). */
      volverA={destinoAlSalir}
    >
      {/* Versión nueva en espera: aviso NO bloqueante, nunca una recarga por
          debajo de la captura (RF-085, RN-130b, RN-131). */}
      <CintaDeVersionNueva />

      <BandaDeRestauracion
        restauradoHace={borrador.restauradoHace}
        alDescartar={borrador.descartarBorrador}
      />

      {confirmandoCancelacion && (
        <div className="aviso w" role="status" data-prueba="confirmar-cancelacion">
          <b>¿Descartar lo capturado?</b> Cancelar borra el borrador guardado en este dispositivo y
          <b> pierdes lo escrito</b>. Salir sin cancelar lo conserva.
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn" type="button" onClick={confirmarCancelacion} data-prueba="confirmar-descartar">
              Sí, descartar
            </button>
            <button className="btn sec" type="button" onClick={() => setConfirmandoCancelacion(false)}>
              Seguir capturando
            </button>
          </div>
        </div>
      )}

      <AvisoDeError error={error} />

      {/* RF-174 · La confirmación de la corrección es la pieza ÚNICA del núcleo, con
          los textos de RN-538 ya resueltos en castellano por `logica/`. El botón de
          aceptar queda HABILITADO también con saldo resultante negativo: se guarda
          avisando (D-294, RF-186, RN-537). */}
      {hayCorreccion && vistaPrevia && (() => {
        const textos = textosDeLaConfirmacion(cuenta, vistaPrevia, formulario.motivoDeCorreccion);
        return (
          <ConfirmacionGeneral
            abierta={confirmandoCorreccion}
            pregunta={textos.pregunta}
            consecuencia={textos.consecuencia}
            detalle={textos.detalle}
            advertencia={textos.advertencia}
            rotuloDeAceptar={textos.rotuloDeAceptar}
            rotuloEnCurso={textos.rotuloEnCurso}
            tono={TONOS_DE_CONFIRMACION.DESTRUCTIVA}
            enCurso={guardando}
            alAceptar={guardar}
            alCancelar={() => setConfirmandoCorreccion(false)}
            datoDePrueba="confirmacion-de-correccion"
          />
        );
      })()}

      {/* Incremento 18/21 · Lo primero que hay que entender de esta pantalla:
          separar la deuda NO la cambia (RN-699). Solo en tarjeta, y solo cuando ya
          existe, que es donde se corrige. */}
      {esCredito && !esAlta && (
        <div className="aviso i" data-prueba="separar-no-cambia-la-deuda">
          <b>Separar tu deuda no la cambia.</b> Lo que debes en total sigue siendo lo mismo, al
          centavo. Lo que haces aquí es decir qué parte ya te está cobrando el banco y qué parte son
          compras a meses que te cobrará más adelante.
        </div>
      )}

      <form onSubmit={intentarGuardar}>
        <div className="card">
          <h3>Datos de la cuenta</h3>

          <label htmlFor="nombre">Nombre · obligatorio</label>
          <input id="nombre" value={formulario.nombre} onChange={cambiar('nombre')} maxLength={100} required />
          <span className="ayuda">Único entre tus cuentas. La otra persona sí puede tener una con el mismo nombre.</span>

          <label htmlFor="idTipoCuenta">Tipo de cuenta · obligatorio</label>
          <select
            id="idTipoCuenta"
            value={formulario.idTipoCuenta}
            onChange={cambiar('idTipoCuenta')}
            /* D-471 · en un préstamo el tipo se presenta FIJO: no hay destino al
               que llevarlo (RN-794). En lo demás manda D-23, sin cambio. */
            disabled={monedaYTipoCongelado || esPrestamoEnEdicion}
            data-prueba="editar-cuenta-tipo"
          >
            {tiposOfrecidos.map((tipo) => (
              <option key={tipo.idTipoCuenta} value={tipo.idTipoCuenta}>
                {tipo.nombre}{tipo.esCredito && tipo.clave === CLAVE_CREDITO ? ' (tarjeta)' : ''}
              </option>
            ))}
          </select>

          {/* D-470 / RN-793 · LA SALIDA AL ALTA EN DOS PASOS, justo donde la
              persona buscaba el tipo que no está. Sin esto, el préstamo
              desaparecería del alta sin decir a dónde fue. */}
          {esAlta && (
            <span className="ayuda" data-prueba="editar-cuenta-enlace-prestamo">
              <Link to="/prestamos/nuevo">¿Es un préstamo? Dalo de alta aquí</Link>, para capturar
              junto el monto original y cuánto debes hoy.
            </span>
          )}

          {/* D-471 · en edición de un préstamo, el porqué de que el tipo no se
              toque, en lenguaje llano y con la salida real si fue un error. */}
          {esPrestamoEnEdicion && (
            <span className="ayuda" data-prueba="editar-cuenta-tipo-prestamo-fijo">
              El tipo de un préstamo no se cambia. Si lo diste de alta por error, cancela su plan de
              pagos, corrige su saldo a cero y desactívalo.
            </span>
          )}
          {/* Fila 34 del inventario (D-334): infringía por los dos lados —el código
              «(S-22)» (RN-627a) y «catálogo sembrado» (RN-627e ampliado)—. El hecho útil
              se conserva dicho en lenguaje de producto (D-212, RN-629). La segunda frase
              ya era conforme y queda intacta: VERIFICADA contra el código, AHORRO no tiene
              ninguna rama propia en la lógica y su única diferencia observable con DEBITO
              son el Nombre y el Icono del catálogo (RN-068, RN-068a, D-19). */}
          <span className="ayuda" data-prueba="editar-cuenta-tipo-ayuda">
            Los tipos de cuenta son fijos: no se pueden crear ni cambiar desde la aplicación.{' '}
            <b>Ahorro se comporta igual que Débito</b>: cambia la etiqueta y el ícono, nada más.
          </span>

          <label htmlFor="idMoneda">Moneda · obligatoria</label>
          <select id="idMoneda" value={formulario.idMoneda} onChange={cambiar('idMoneda')} disabled={monedaYTipoCongelado}>
            {monedas.map((moneda) => (
              <option key={moneda.idMoneda} value={moneda.idMoneda}>{moneda.nombre} ({moneda.codigoISO})</option>
            ))}
          </select>
          <span className="ayuda">
            {monedaYTipoCongelado
              ? 'Esta cuenta ya tiene movimientos: la moneda ya no se puede cambiar. Para cambiar de moneda, desactívala y crea otra.'
              : 'Una sola moneda por cuenta, sin conversión.'}
          </span>

          {/* H-17 · EL CAMPO YA NO SE DESHABILITA NUNCA (RF-171, RN-533). Y con él se
              fue el texto que mandaba registrar un movimiento de ajuste: dejarlo
              vivo sería instruir a la persona para que haga algo que ya no hace
              falta (RN-535). */}
          <label htmlFor="saldoInicial">{esCredito ? 'Deuda inicial' : 'Saldo inicial'} · obligatorio</label>
          <CampoDeImporte
            id="saldoInicial"
            valor={formulario.saldoInicial}
            alCambiar={cambiarImporte('saldoInicial')}
            codigoDivisa={divisaDelContexto}
            contexto="esta cuenta"
            alCambiarValidez={marcarValidez('saldoInicial')}
            ayuda={esCredito
              ? 'Cero o positivo, con dos decimales. Es lo que debías el día que diste de alta la tarjeta, antes de registrar movimientos.'
              : 'Cero o positivo, con dos decimales. Es la cifra que tenía la cuenta el día que la diste de alta, antes de registrar movimientos.'}
          />

          {/* ===== DIFERIDO DECLARADO · incremento 18/21 =======================
              RF-248 (alta) y RF-249 (corrección). UN SOLO IMPORTE TOTAL POR
              TARJETA, sin desglose por compra (D-389): el desglose fino llega con
              los planes reales.

              SOLO EN TARJETA DE CRÉDITO (RN-690): en cualquier otra cuenta el
              campo NO SE OFRECE y no se envía.

              RNF-133 · Se consume el campo de importe ÚNICO del núcleo, que ya
              conserva el valor y lo deja seleccionado completo al enfocar (RN-135,
              RN-659). No se reimplementa nada y el campo JAMÁS se deshabilita. */}
          {esCredito && (
            <div data-prueba="campo-de-diferido-declarado">
              <label htmlFor="diferidoDeclarado">
                De esa deuda, ¿cuánto son compras a meses que el banco todavía no te cobra? · opcional
              </label>
              <CampoDeImporte
                id="diferidoDeclarado"
                valor={formulario.diferidoDeclarado}
                alCambiar={cambiarImporte('diferidoDeclarado')}
                codigoDivisa={divisaDelContexto}
                contexto="las compras a meses de esta tarjeta"
                alCambiarValidez={marcarValidez('diferidoDeclarado')}
                ayuda={'Si no tienes compras a meses, déjalo en 0. Pon aquí solo las que todavía no has '
                  + 'registrado como plan: si ya registraste su plan, no la pongas, se descontaría dos veces.'}
              />

              {/* Aviso MIENTRAS SE ESCRIBE, para no descubrir el rechazo al
                  guardar. El rechazo real es del servidor (RN-688). */}
              {reparto?.cabe === false && (
                <div className="aviso e" role="alert" data-prueba="diferido-excede-la-deuda">
                  <b>Lo que está a meses no puede ser mayor que tu deuda.</b> Las compras a meses son
                  parte de esa deuda: debes{' '}
                  {formatearImporte(reparto.deudaTotal, cuenta?.codigoISO ?? divisaDelContexto)} y
                  pusiste {formatearImporte(reparto.diferido, cuenta?.codigoISO ?? divisaDelContexto)} a
                  meses. Corrige las dos cifras juntas: baja lo que está a meses o sube la deuda.
                </div>
              )}

              {/* RN-706 / D-391 · La deuda bajó después de declarar. Nada se
                  ajustó solo: se dice y se pide corregirlo aquí mismo. */}
              {diferidoFueraDeRango && reparto?.cabe !== false && (
                <div className="aviso w" role="status" data-prueba="diferido-fuera-de-rango">
                  <b>Lo que tienes a meses es mayor que tu deuda de hoy.</b> Tu deuda bajó después de
                  que lo declaraste y nada se cambió por su cuenta. Corrige la cifra de compras a meses
                  cuando puedas.
                </div>
              )}
            </div>
          )}

          {/* RN-558, RN-560 · El motivo aparece SOLO si el importe cambió de verdad,
              y se limpia al volver al valor original. Es opcional: si se deja vacío
              la corrección se guarda igual y la auditoría queda completa con quién,
              cuándo y de cuánto a cuánto. */}
          {hayCorreccion && (
            <div data-prueba="campo-motivo-de-correccion">
              <label htmlFor="motivoDeCorreccion">¿Por qué lo corriges? · opcional</label>
              {/* rows=3 es la altura inicial: tres líneas para que se vea que
                  cabe una explicación y se pueda escribir con comodidad en el
                  teléfono. El estilo (ancho completo, borde y esquinas) lo pone
                  la regla de entrada del núcleo en estilos/base.css; aquí no se
                  declara ningún estilo propio. */}
              <textarea
                id="motivoDeCorreccion"
                rows={3}
                value={formulario.motivoDeCorreccion}
                onChange={cambiar('motivoDeCorreccion')}
                placeholder="Por ejemplo: capturé el saldo de otro mes."
              />
              <div className="ayuda" style={{ textAlign: 'right' }} data-prueba="contador-de-motivo">
                {motivoEscrito.length} / {LONGITUD_MAXIMA_DEL_MOTIVO}
              </div>
              {motivoSePasa && (
                <div className="aviso e" data-prueba="motivo-demasiado-largo">
                  El motivo no puede pasar de {LONGITUD_MAXIMA_DEL_MOTIVO} caracteres.
                </div>
              )}
              <span className="ayuda">
                Puedes dejarlo vacío: la corrección se guarda igual y queda registrada con quién la
                hizo, cuándo y de cuánto a cuánto. Si escribes algo, se guarda junto a esta corrección
                y se lee en su historial; <b>no se mezcla con las notas de la cuenta</b>.
              </span>
            </div>
          )}
        </div>

        {/* RF-248 / RF-249 · CÓMO QUEDA REPARTIDA LA DEUDA INICIAL. Las tres
            cifras se derivan al vuelo y ninguna se guarda (RN-687). Separar NO
            cambia lo que debes, y el renglón de la suma está para que se vea
            (RN-699). */}
        {esCredito && <RepartoDeLaDeuda reparto={reparto} codigoISO={cuenta?.codigoISO ?? divisaDelContexto} />}

        {/* RF-173 · CÓMO QUEDA SI GUARDAS. Tres cifras calculadas con lo que el
            servidor YA devuelve; ninguna llamada nueva. */}
        {hayCorreccion && vistaPrevia && (
          <div className="card" style={{ margin: 12 }} data-prueba="vista-previa-de-correccion">
            <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>CÓMO QUEDA SI GUARDAS</h4>

            <div className="fila">
              <span className="crece">{esCredito ? 'Deuda inicial' : 'Saldo inicial'} de ahora</span>
              <b>{formatearImporte(vistaPrevia.anterior, cuenta?.codigoISO)}</b>
            </div>
            <div className="fila">
              <span className="crece">{esCredito ? 'Deuda inicial' : 'Saldo inicial'} corregido</span>
              <b>{formatearImporte(vistaPrevia.nuevo, cuenta?.codigoISO)}</b>
            </div>
            <div className="fila">
              <span className="crece">{esCredito ? 'Deuda de la tarjeta' : 'Saldo de la cuenta'}</span>
              <b data-prueba="saldo-resultante">{formatearImporte(vistaPrevia.resultante, cuenta?.codigoISO)}</b>
            </div>

            {/* RN-537c · La advertencia aparece SOLO cuando toca y NO SE QUEDA
                PEGADA: se recalcula en cada pulsación, así que desaparece sola en
                cuanto el resultado deja de ser negativo. Con resultado en cero no se
                pinta ninguna: ahí no hay nada que advertir (RN-538). */}
            {advertencia && (
              <div className={`aviso ${advertencia.tono}`} data-prueba="advertencia-del-resultado">
                {advertencia.texto}
              </div>
            )}

            {vistaPrevia.estado === ESTADOS_DEL_RESULTADO.CERO && (
              <div className="aviso ok" data-prueba="resultado-en-cero">
                Queda exactamente en {formatearImporte('0.00', cuenta?.codigoISO)}. Con el saldo en cero,
                esta {esCredito ? 'tarjeta' : 'cuenta'} ya se podría desactivar si quisieras.
              </div>
            )}

            <span className="ayuda">
              Tus movimientos no cambian: no se reescribe ninguno, no se cancela ninguno y no se crea
              ninguno. El saldo cambia porque es una suma que empieza en esta cifra.
            </span>
          </div>
        )}

        {/* D-28 / RN-070g.iv, PRECISADO POR RN-752: los datos del plástico existen en
            Débito, Ahorro y Crédito —LISTA EXPLÍCITA—; no en Efectivo y tampoco en un
            PRÉSTAMO, que no tiene plástico que capturar. Ocultarlos es ayuda visual: el
            servidor los rechaza igual con DATOS_DE_TARJETA_NO_APLICAN. */}
        {admitePlastico && (
          <div className="card">
            <h3>Datos del plástico <span className="chip">débito · ahorro · crédito</span></h3>

            <label htmlFor="cuatroUltimosDigitos">Cuatro últimos dígitos · opcional</label>
            <input
              id="cuatroUltimosDigitos"
              inputMode="numeric"
              maxLength={4}
              value={formulario.cuatroUltimosDigitos}
              onChange={cambiar('cuatroUltimosDigitos')}
              placeholder="1847"
            />
            <span className="ayuda">
              Solo ayuda visual. <b>No se guarda número completo, CLABE, CVV ni NIP</b>.
            </span>

            <label htmlFor="vigenciaMes">Vigencia del plástico · opcional · las dos o ninguna</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input id="vigenciaMes" inputMode="numeric" maxLength={2} placeholder="Mes (1-12)"
                value={formulario.vigenciaMes} onChange={cambiar('vigenciaMes')} />
              <input aria-label="Año de vigencia" inputMode="numeric" maxLength={4} placeholder="Año (2029)"
                value={formulario.vigenciaAnio} onChange={cambiar('vigenciaAnio')} />
            </div>
            <span className="ayuda">
              Solo dice cuándo tocará renovar. <b>No caduca la cuenta, no la desactiva y no genera
              ningún aviso</b>. Una vigencia ya pasada es un dato válido.
            </span>
          </div>
        )}

        {/* Fila 1 del inventario del incremento 15 (RF-222/RF-223, D-325): el recuadro
            informativo de efectivo se retiró completo y sin sustituto. Justificaba el diseño
            («así que no se piden») y citaba el código de API DATOS_DE_TARJETA_NO_APLICAN.
            Mismo criterio aprobado para su gemelo del detalle de cuenta (D-252, RF-154:
            «en su lugar no queda nada»). La pantalla ya oculta esos campos en efectivo. */}

        {esEfectivo && teniaPlastico && (
          <div className="aviso w">
            <b>Cambiaste el tipo a Efectivo y esta cuenta tenía datos de plástico.</b> Al guardar, los
            cuatro últimos dígitos y la vigencia <b>se borran en la misma operación</b>, con el mismo
            criterio con el que se borran los datos de tarjeta al salir del tipo crédito.
          </div>
        )}

        {esCredito && (
          <div className="card">
            <h3>Datos propios de la tarjeta de crédito</h3>

            <label htmlFor="limiteCredito">Límite de crédito · obligatorio</label>
            <CampoDeImporte
              id="limiteCredito"
              placeholder="45000.00"
              valor={formulario.limiteCredito}
              alCambiar={cambiarImporte('limiteCredito')}
              codigoDivisa={divisaDelContexto}
              contexto="esta tarjeta"
              alCambiarValidez={marcarValidez('limiteCredito')}
              ayuda="Mayor que cero, en la moneda de la tarjeta."
            />

            <label htmlFor="diaCorte">Día de corte y día límite de pago · obligatorios · día del mes, 1 a 31</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input id="diaCorte" inputMode="numeric" maxLength={2} placeholder="Corte (5)"
                value={formulario.diaCorte} onChange={cambiar('diaCorte')} />
              <input aria-label="Día límite de pago" inputMode="numeric" maxLength={2} placeholder="Pago (25)"
                value={formulario.diaLimitePago} onChange={cambiar('diaLimitePago')} />
            </div>
            <span className="ayuda">
              Se capturan como <b>día del mes</b>, no como fecha: la fecha concreta se calcula cada mes.
              {simulacion && (
                <>
                  {' '}Corte el {formulario.diaCorte} ({formatearFecha(simulacion.fechaCorteDelPeriodo)}),
                  pagas el {formulario.diaLimitePago} ({formatearFecha(simulacion.fechaLimitePago)}):
                  {' '}<b>{simulacion.diasDeGracia} días de gracia</b>. Lo calcula el servidor.
                </>
              )}
            </span>

            <div className="aviso i">
              <b>Los días de gracia no se capturan</b>, se calculan. Si el día no existe en el
              mes, la fecha <b>se recorre al último día</b>: un corte el 31 cae el 28 de febrero, o el
              29 en año bisiesto. Un día de pago <b>menor o igual</b> al de corte es válido y
              frecuente: la fecha límite cae el mes siguiente.
            </div>
          </div>
        )}

        <div className="card">
          <h3>Opcional</h3>
          <label htmlFor="notas">Notas</label>
          <textarea id="notas" maxLength={500} value={formulario.notas} onChange={cambiar('notas')}
            placeholder="Para qué usas esta cuenta" />
        </div>

        {/* Fila 31 del inventario del incremento 15 (RF-222, D-330, rev. 4): salieron el
            catálogo de ausencias («no hay campo de orden ni pantalla de reordenar») y el
            sujeto técnico («lo resuelve el servidor»). El hecho se conserva porque es
            cierto y útil (D-29 / RN-071) y se le añade la consecuencia que importa justo
            aquí: renombrar mueve la cuenta de lugar. El recuadro no se mueve de sitio. */}
        <div className="aviso i" data-prueba="editar-cuenta-orden-alfabetico">
          Las cuentas se presentan en <b>orden alfabético por nombre</b>: si cambias el
          nombre, esta cuenta cambiará de lugar en la lista.
        </div>

        <div style={{ display: 'grid', gap: 8, margin: 12 }} data-prueba="editar-cuenta-acciones">
          {/* D-294 · El botón se deshabilita por DATO INVÁLIDO, nunca porque el saldo
              resultante quede en negativo: eso se guarda avisando (RF-186, RN-537). */}
          <button className="btn" type="submit"
            disabled={guardando || hayImporteInvalido || motivoSePasa || noCabeElDiferido}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          <button className="btn sec" type="button" onClick={cancelar}>
            Cancelar
          </button>
          <MarcaDeBorrador sucio={borrador.sucio} />
        </div>
      </form>
    </Shell>
  );
}
